import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import {
  BreadcrumbNav,
  Button,
  Dropdown,
  PageHeader,
  ScrollTabs,
  Skeleton,
  StatusMessage,
  useToast,
} from '@aimsos/ui';
import { LayoutGrid } from 'lucide-react';
import { useWorkspaces } from '../context';
import {
  useWorkspaceActivateToggle,
  useWorkspaceDetailQuery,
  useWorkspaceDisplayName,
} from '../hooks';
import { ChangeWorkspaceOwnerModal, WorkspaceStatusConfirmModal } from '../components';
import { mapWorkspaceOverviewToListItem } from '../utils';

/**
 * Workspace detail layout.
 * Renders BreadcrumbNav + ScrollTabs; child routes render in <Outlet />.
 *
 * Routes (relative to /workspaces/:workspaceId):
 *   index → redirect to overview
 *   overview
 *   users
 *   activity-log
 */
const WorkspaceDetailPage = () => {
  const { t } = useTranslation('workspaces');
  const { workspaceId } = useParams();
  const { workspaces, listLoading, listRefreshKey, refetchWorkspaces } = useWorkspaces();
  const displayName = useWorkspaceDisplayName();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const workspaceFromList = workspaces.find((ws) => ws.id === workspaceId) ?? null;

  const [actionsMenuKey, setActionsMenuKey] = useState(0);
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);
  const [workspaceStatusModal, setWorkspaceStatusModal] = useState(null);

  const {
    workspaceDetail,
    workspaceDetailLoading,
    workspaceDetailError,
    refreshWorkspaceDetail,
  } = useWorkspaceDetailQuery({ workspaceId, listRefreshKey });

  const workspaceFromDetail =
    !workspaceFromList &&
    workspaceId &&
    !workspaceDetailLoading &&
    workspaceDetail
      ? mapWorkspaceOverviewToListItem(workspaceId, workspaceDetail)
      : null;

  const workspace = workspaceFromList ?? workspaceFromDetail;

  useEffect(() => {
    const id = requestAnimationFrame(() => setWorkspaceStatusModal(null));
    return () => cancelAnimationFrame(id);
  }, [workspaceId]);

  const statusReady =
    workspace != null && String(workspace.status ?? '').trim().toLowerCase() === 'ready';

  const bumpActionsMenu = useCallback(() => setActionsMenuKey((k) => k + 1), []);

  const tenantIsActive =
    workspaceDetail != null
      ? Boolean(workspaceDetail.isActive)
      : workspaceDetailLoading
        ? undefined
        : Boolean(workspace?.isActive ?? false);

  const layoutListLoading =
    listLoading || (!workspaceFromList && workspaceId && workspaceDetailLoading);

  const outletContext = useMemo(
    () => ({
      workspace,
      loading: layoutListLoading,
      workspaceDetail: {
        data: workspaceDetail,
        loading: workspaceDetailLoading,
        error: workspaceDetailError,
        refresh: refreshWorkspaceDetail,
      },
    }),
    [
      workspace,
      layoutListLoading,
      workspaceDetail,
      workspaceDetailLoading,
      workspaceDetailError,
      refreshWorkspaceDetail,
    ],
  );

  const onWorkspaceStatusSuccess = useCallback(() => {
    bumpActionsMenu();
    refreshWorkspaceDetail();
  }, [bumpActionsMenu, refreshWorkspaceDetail]);

  const { commitWorkspaceStatusChange, actionBusy } = useWorkspaceActivateToggle({
    workspaceId,
    workspace,
    statusReady,
    refetchWorkspaces,
    onSuccess: onWorkspaceStatusSuccess,
  });

  const workspaceConfirmName = useMemo(() => {
    if (!workspace) return '';
    const n = workspace.name?.trim();
    if (n) return n;
    return displayName(workspace);
  }, [workspace, displayName]);

  const handleConfirmWorkspaceStatus = useCallback(async () => {
    if (!workspaceStatusModal || !workspace) return;
    const activating = workspaceStatusModal === 'activate';
    try {
      const ok = await commitWorkspaceStatusChange(activating);
      if (!ok) return;
      setWorkspaceStatusModal(null);
      const name = displayName(workspace);
      if (activating) {
        toast.success({
          title: t('detail.workspaceStatusToasts.reactivatedTitle', { name }),
          message: t('detail.workspaceStatusToasts.reactivatedMessage'),
          canClose: true,
        });
      } else {
        toast.success({
          title: name,
          message: t('detail.workspaceStatusToasts.deactivatedMessage'),
          canClose: true,
        });
      }
    } catch (err) {
      toast.error({
        title: t('detail.workspaceStatusToasts.errorTitle'),
        message: err?.message ?? t('detail.actions.error'),
        canClose: true,
      });
    }
  }, [
    workspaceStatusModal,
    workspace,
    commitWorkspaceStatusChange,
    displayName,
    toast,
    t,
  ]);

  const isTab = useCallback(
    (path) => {
      const segments = location.pathname.split('/');
      return segments[segments.length - 1] === path;
    },
    [location.pathname],
  );

  const goTo = useCallback(
    (path) => navigate(`/workspaces/${workspaceId}/${path}`),
    [navigate, workspaceId],
  );

  const onBreadcrumbNavigate = useCallback((path) => navigate(path), [navigate]);

  const notFound =
    Boolean(workspaceId) && !listLoading && !workspaceDetailLoading && !workspace;

  const breadcrumbItems = useMemo(() => {
    const root = { label: t('nav.workspaces'), path: '/workspaces' };
    if (notFound) {
      return [root, { label: t('detail.notFound.breadcrumb') }];
    }
    if ((listLoading || workspaceDetailLoading) && !workspace) {
      return [
        root,
        {
          label: (
            <span className="inline-flex min-w-[140px] align-middle">
              <Skeleton height="16px" width="160px" borderRadius="4px" />
            </span>
          ),
        },
      ];
    }
    return [root, { label: displayName(workspace) }];
  }, [t, notFound, listLoading, workspaceDetailLoading, workspace, displayName]);

  if (notFound) {
    return (
      <div className="flex h-full flex-col overflow-hidden bg-surface-base">
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-base">
          <div className="flex w-full shrink-0 flex-col gap-6 p-6">
            <BreadcrumbNav
              className="min-w-0"
              items={breadcrumbItems}
              onNavigate={onBreadcrumbNavigate}
            />
          </div>
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-6 pb-12">
            <StatusMessage
              icon={LayoutGrid}
              title={t('detail.notFound.title')}
              subtitle={workspaceDetailError ?? t('detail.notFound.description')}
            />
            <Button variant="secondary" size="medium" onClick={() => navigate('/workspaces')}>
              {t('detail.notFound.backButton')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-surface-base">
      {/* Page header + tab routes: header is fixed height; tab content scrolls inside Outlet (see drives list). */}
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-base">
        <div className="flex w-full shrink-0 flex-col gap-6 p-6 pb-0">
          <header className="flex flex-col gap-4">
            <BreadcrumbNav
              className="min-w-0"
              items={breadcrumbItems}
              onNavigate={onBreadcrumbNavigate}
            />

            {workspace ? (
              <PageHeader
                className="!px-0 border-0 py-1"
                icon={<LayoutGrid className="h-5 w-5 text-teal-600 dark:text-teal-400" />}
                title={displayName(workspace)}
                actions={
                  <div className="flex flex-col items-end gap-1">
                    <Dropdown
                      key={actionsMenuKey}
                      label={t('detail.actions.button')}
                      variant="primary"
                      size="medium"
                      alignMenu="right"
                      disabled={actionBusy}
                      menuMaxHeight="none"
                    >
                      {statusReady ? (
                        <>
                          <Dropdown.Item
                            onClick={() => {
                              setWorkspaceStatusModal(tenantIsActive ? 'deactivate' : 'activate');
                              setActionsMenuKey((k) => k + 1);
                            }}
                            disabled={actionBusy || tenantIsActive === undefined}
                          >
                            {tenantIsActive
                              ? t('detail.actions.deactivateWorkspace')
                              : t('detail.actions.activateWorkspace')}
                          </Dropdown.Item>
                          <Dropdown.Separator />
                        </>
                      ) : null}
                      <Dropdown.Item
                        onClick={() => {
                          setChangeOwnerOpen(true);
                          setActionsMenuKey((k) => k + 1);
                        }}
                        disabled={actionBusy}
                      >
                        {t('detail.actions.changeOwner')}
                      </Dropdown.Item>
                    </Dropdown>
                  </div>
                }
              />
            ) : null}

            {/* ScrollTabs — same API as DashboardListHeader */}
            <ScrollTabs>
              <ScrollTabs.Item
                active={isTab('overview')}
                onClick={() => goTo('overview')}
              >
                {t('detail.tabs.overview')}
              </ScrollTabs.Item>
              <ScrollTabs.Item
                active={isTab('users')}
                onClick={() => goTo('users')}
              >
                {t('detail.tabs.users')}
              </ScrollTabs.Item>
              <ScrollTabs.Item
                active={isTab('activity-log')}
                onClick={() => goTo('activity-log')}
              >
                {t('detail.tabs.activityLog')}
              </ScrollTabs.Item>
            </ScrollTabs>
          </header>
        </div>

        {/* Tab content — fills remaining height; each tab owns internal scroll (e.g. users table). */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6">
          <Outlet context={outletContext} />
        </div>

        {workspaceId ? (
          <ChangeWorkspaceOwnerModal
            isOpen={changeOwnerOpen}
            onClose={() => setChangeOwnerOpen(false)}
            workspaceId={workspaceId}
            onUpdated={() => {
              refetchWorkspaces();
              refreshWorkspaceDetail();
            }}
          />
        ) : null}

        {workspace && workspaceStatusModal ? (
          <WorkspaceStatusConfirmModal
            isOpen
            mode={workspaceStatusModal === 'activate' ? 'activate' : 'deactivate'}
            expectedConfirmName={workspaceConfirmName}
            onClose={() => {
              if (!actionBusy) setWorkspaceStatusModal(null);
            }}
            onConfirm={handleConfirmWorkspaceStatus}
            submitting={actionBusy}
          />
        ) : null}
      </main>
    </div>
  );
};

export default WorkspaceDetailPage;
